/**
 * localStorage черновик формы Block 10 «Что улучшить?» (PRD §14, ADR-0018 §Q4).
 *
 * **Ключ:** `pomoshnik-feedback-draft-v1` (версионированный — при breaking
 * change бампать до v2, старый ключ оставлять 30 дней для cleanup).
 *
 * **Что persisted:**
 * - `bodyMd` — текст
 * - `tags` — массив выбранных тегов
 * - `customTag` — если 'other' в tags
 * - `savedAt` — Date.now() для TTL
 *
 * **Что НЕ persisted (P-009):**
 * - `attachedPhotosR2Keys` — R2 signed URL'ы 3600s expiry, после рестарта недействительны.
 * - `screenshotR2Key` — auto-screenshot opt-in пересоздаётся явно (нельзя
 *   автоматически добавить старый чекбокс «согласен на скриншот»).
 * - `blockId` / `screenId` — context навигации; при возврате на форму
 *   приходят из `useLocation().state` (или fallback 'unknown').
 *
 * **TTL:** 7 дней. Draft старше игнорируется (loadDraft возвращает null,
 * silently — в Phase 9+ можно добавить Sentry breadcrumb).
 *
 * **Quota fallback:** `saveDraft` catches `QuotaExceededError` и возвращает
 * `false` без throw. UI Phase 4 может показать ненавязчивый toast «черновик
 * не сохранён» (но это nice-to-have, не блокер).
 *
 * References:
 * - ADR-0018 §Q4 (localStorage draft pattern)
 * - PRD §14 (Block 10 form)
 * - P-009 (минимизация — R2 keys + screenshot не персистим)
 */

import type { FeedbackTag } from '@pomoshnik/shared';

export const FEEDBACK_DRAFT_KEY = 'pomoshnik-feedback-draft-v1';
export const FEEDBACK_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export interface FeedbackDraft {
  bodyMd: string;
  tags: FeedbackTag[];
  customTag?: string;
  savedAt: number; // Date.now()
}

export function loadDraft(now: number = Date.now()): FeedbackDraft | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  const raw = localStorage.getItem(FEEDBACK_DRAFT_KEY);
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Malformed JSON — clear and return null (defensive).
    localStorage.removeItem(FEEDBACK_DRAFT_KEY);
    return null;
  }
  if (!isDraftShape(parsed)) {
    localStorage.removeItem(FEEDBACK_DRAFT_KEY);
    return null;
  }
  if (now - parsed.savedAt > FEEDBACK_DRAFT_TTL_MS) {
    // Stale — clear and return null.
    localStorage.removeItem(FEEDBACK_DRAFT_KEY);
    return null;
  }
  return parsed;
}

export function saveDraft(
  draft: Omit<FeedbackDraft, 'savedAt'>,
  now: number = Date.now(),
): boolean {
  if (typeof localStorage === 'undefined') {
    return false;
  }
  const persisted: FeedbackDraft = { ...draft, savedAt: now };
  try {
    localStorage.setItem(FEEDBACK_DRAFT_KEY, JSON.stringify(persisted));
    return true;
  } catch {
    // QuotaExceededError or SecurityError (private browsing) — silent fail.
    return false;
  }
}

export function clearDraft(): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.removeItem(FEEDBACK_DRAFT_KEY);
}

function isDraftShape(value: unknown): value is FeedbackDraft {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.bodyMd !== 'string') return false;
  if (!Array.isArray(v.tags)) return false;
  if (!v.tags.every((t) => typeof t === 'string')) return false;
  if (v.customTag !== undefined && typeof v.customTag !== 'string') return false;
  if (typeof v.savedAt !== 'number') return false;
  return true;
}
